import { API } from '../../api';
import { Button, Input, List, Modal, Select } from 'antd';
import { React, useEffect, useState } from 'react';
import { setStickyDropdownSelection } from '../../utils/util';
import { useHistory } from 'react-router-dom';
import DataSourceCreate from '../dataSources/dataSourceCreate';

const { Option } = Select;

const statusToColorMap = {
  'Mappings Created': {
    color: '#cf1322',
    border: '#ffa39e',
    background: '#fff1f0',
  },
  'Review Created': {
    color: '#d46b08',
    border: '#ffd591',
    background: 'fff7e6',
  },
  'Review Submitted': {
    color: '#096dd9',
    border: '#91d5ff',
    background: '#e6f7ff',
  },
  Validated: {
    color: '#389e0d',
    border: '#b7eb8f',
    background: '#f6ffed',
  },
};

/**
 * This component lists all of the files for a given client.
 * The user is able to filter based on stage.
 * @returns
 */
const Summary = props => {
  const { client } = props;
  const clientId = client.id;

  /**
   * The stage of the review process:
   * all - all files are shown
   * mappings_created - no review exists
   * in_progress - a review has been created but has not been submitted for review
   * in_review - review has been submitted for review
   * validated - review has been completed
   */
  const [stage, setStage] = useState('all');
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [resourceTypeOptions, setResourceTypeOptions] = useState([]);
  const [mappingGroups, setMappingGroups] = useState([]);
  const [displayMappingGroups, setDisplayMappingGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const history = useHistory();

  const generateReviewStatusDisplayText = group => {
    let displayText = '';
    const statusToTextMap = {
      'in progress': 'Review Created',
      'in review': 'Review Submitted',
      complete: 'Validated',
    };

    if (group.review_status) {
      displayText = statusToTextMap[group.review_status];
    } else {
      displayText = 'Mappings Created';
    }

    return displayText;
  };

  /**
   * Get distinct resource types
   */
  const getResourceTypes = () => {
    if (mappingGroups.length === 0) {
      return [];
    }

    const resourceTypes = [];

    for (const group of mappingGroups) {
      if (!resourceTypes.includes(group.resourcetype)) {
        resourceTypes.push(group.resourcetype);
      }
    }

    return setResourceTypeOptions(resourceTypes);
  };

  /**
   * Get all mapping groups in the given stage for the client.
   */
  const getMappingGroups = async () => {
    const mappingGroupsResponse = await API.get(`/meta/mappinggroups?client_id=${clientId}`);

    const mappingGroupList = mappingGroupsResponse.data.map(group => {
      const annotatedGroup = Object.assign(group);
      annotatedGroup.review_status_display_text = generateReviewStatusDisplayText(group);
      return annotatedGroup;
    });

    mappingGroupList.sort(
      (a, b) =>
        a.resourcetype.localeCompare(b.resourcetype) ||
        a.namespace.localeCompare(b.namespace) ||
        a.file.localeCompare(b.file) ||
        a.version.localeCompare(b.version),
    );

    return mappingGroupList;
  };

  const fetchData = async () => {
    setLoading(true);

    const mappingGroupsList = await getMappingGroups();
    setMappingGroups(mappingGroupsList);
    setDisplayMappingGroups(mappingGroupsList);

    setLoading(false);
  };

  const filterDisplayData = () => {
    const selectValToReviewStatusMap = {
      review_created: 'in progress',
      in_review: 'in review',
      validated: 'complete',
      mappings_created: null,
    };
    const filteredGroups = mappingGroups.filter(group => {
      return (
        group.review_status === selectValToReviewStatusMap[stage] &&
        group.resourcetype === selectedResourceType
      );
    });
    setDisplayMappingGroups(filteredGroups);
  };

  useEffect(() => {
    filterDisplayData();
  }, [stage, selectedResourceType]);

  useEffect(() => {
    getResourceTypes();
  }, [mappingGroups]);

  useEffect(() => {
    fetchData();
  }, []);

  const onSelectStage = value => {
    setStage(value);
  };

  const onSelectResourceType = value => {
    setSelectedResourceType(value);
  };

  const searchMappingGroups = event => {
    const searchTerm = event.target.value;
    const filteredMappingGroups = displayMappingGroups.filter(
      group => group.file.includes(searchTerm) || group.version.includes(searchTerm),
    );
    setDisplayMappingGroups(filteredMappingGroups);
  };

  const onCreateDataSourceClick = () => setShowModal(true);

  const closeModalNew = () => setShowModal(false);

  const addDataSource = () => fetchData();

  const handleGroupClick = (file, resourcetype, namespace, version) => {
    setStickyDropdownSelection(clientId, {
      resourcetype,
      namespace,
      file,
      version,
    });

    history.push(`/map/${clientId}/mappings`);
  };

  return (
    <>
      <Modal
        title="Create New Mapping"
        visible={showModal}
        width={1000}
        onCancel={closeModalNew}
        destroyOnClose
        footer={null}
      >
        <DataSourceCreate
          addDataSource={addDataSource}
          client={client}
          closeModal={closeModalNew}
        />
      </Modal>
      <Button onClick={onCreateDataSourceClick} style={{ marginTop: 16, marginBottom: 16 }}>
        Create Data Source
      </Button>
      <List
        dataSource={displayMappingGroups}
        loading={loading}
        bordered
        header={
          <>
            <Input
              data-cy="search-mappings-input"
              style={{ width: '350px' }}
              addonBefore="Search:"
              onChange={searchMappingGroups}
            />
            <div style={{ float: 'right' }}>
              <span style={{ marginRight: '20px' }}>Resource Type:</span>
              <Select
                data-cy="filter-resource-type"
                defaultValue={null}
                style={{ width: '200px' }}
                bordered
                onChange={onSelectResourceType}
              >
                <Option value={null}>All</Option>
                {resourceTypeOptions.map(type => (
                  <Option value={type} key={type} data-cy={`resource-type-option-${type}`}>
                    {type}
                  </Option>
                ))}
              </Select>
              <span style={{ marginLeft: '20px', marginRight: '20px' }}>Review Status:</span>
              <Select
                data-cy="filter-review-status"
                defaultValue="all"
                style={{ width: '150px' }}
                bordered
                onChange={onSelectStage}
              >
                <Option value="all">All</Option>
                <Option value="mappings_created">Mappings Created</Option>
                <Option value="review_created">Review Created</Option>
                <Option value="in_review">Review Submitted</Option>
                <Option value="validated" data-cy="validated-mappings-option">
                  Validated
                </Option>
              </Select>
            </div>
          </>
        }
        renderItem={group => (
          <List.Item
            style={{ paddingLeft: '30px' }}
            extra={
              <div style={{ width: '150px', textAlign: 'right' }}>
                <span
                  style={{
                    border: '1px solid',
                    borderColor: statusToColorMap[group.review_status_display_text].border,
                    padding: '5px 10px',
                    backgroundColor: statusToColorMap[group.review_status_display_text].background,
                    color: statusToColorMap[group.review_status_display_text].color,
                  }}
                >
                  {group.review_status_display_text}
                </span>
              </div>
            }
          >
            <List.Item.Meta
              title={
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    handleGroupClick(group.file, group.resourcetype, group.namespace, group.version)
                  }
                >
                  {group.resourcetype} / {group.namespace} / {group.file} / {group.version}
                </span>
              }
            />
          </List.Item>
        )}
      />
    </>
  );
};

export default Summary;
